import {useEffect, useState, useCallback} from 'react';
import {useSelector} from 'react-redux';

import {
  getConversationTagsService,
  createConversationTagService,
  deleteConversationTagService,
} from '../promises/conversationTag';

import {actions} from '../duck';
import {useActions} from '../../../../utils/globalStore';

import type {RootState} from '../../..';
import type {ConversationTag} from '../../../../entities';

export function useSetInitialConversationTags() {
  const setConversationTags = useActions(actions.setConversationTags);

  useEffect(() => {
    getConversationTagsService().then(conversationTags => {
      setConversationTags(conversationTags.data);
    });
  }, [setConversationTags]);
}

const conversationActions = {
  createConversationTag: actions.addConversationTag,
  editConversationTag: actions.editConversationTag,
  deleteConversationTag: actions.deleteConversationTag,
};

export function useConversationTags() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {createConversationTag, deleteConversationTag} = useActions(
    conversationActions
  );

  const conversationTags: ConversationTag[] = useSelector(
    (state: RootState) => state.chat.conversationTags
  );

  const createTag = useCallback(
    async (tag: string | string[]): ConversationTag[] => {
      setLoading(true);

      try {
        const tagArray = Array.isArray(tag) ? tag : [tag];
        const promises = tagArray.map(tag => createConversationTagService(tag));

        const conversationTags = await Promise.all(promises);

        createConversationTag(conversationTags);

        return conversationTags;
      } catch (error) {
        setError('Error al crear la etiqueta');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [createConversationTag]
  );

  const deleteTag = useCallback(
    async (tagId: string | string[]) => {
      setLoading(true);

      try {
        const tagArray = Array.isArray(tagId) ? tagId : [tagId];
        const promises = tagArray.map(tagId =>
          deleteConversationTagService(tagId)
        );
        await Promise.all(promises);
        deleteConversationTag(tagArray);
      } catch (error) {
        setError('Error al eliminar la etiqueta');
      } finally {
        setLoading(false);
      }
    },
    [deleteConversationTag]
  );

  return {conversationTags, createTag, deleteTag, loading, error};
}
